import { useState } from "react";
import { Link } from "react-router";
import { Bracket } from "@/components/trade/Bracket";
import { useCoinPrice } from "@/data/useCoinPrice";
import { formatPreferredCurrency } from "@/domain/money";
import { targetPriceFromPercent } from "@/domain/trade";
import type { OpenTrade } from "@/domain/types";

/** Fallback so the demo is never empty — replaced as soon as the live price lands. */
const FALLBACK_BTC_USD = 65000;
const QUANTITY = 0.05;

/**
 * The homepage demo: set two lines on a real Bitcoin price and watch the
 * bracket redraw.
 *
 * The 2020 `DemoTrade` showed the same numbers as a grid of Bulma `level`
 * items and asked you to imagine the rest. This drives the actual `Bracket`
 * component — the one the trade detail screen uses — so the page demonstrates
 * the product rather than describing it.
 *
 * It also drops two dependencies. `DemoTrade` pulled in `rc-slider` and
 * `rc-tooltip` plus both stylesheets for a single two-handle range; two native
 * range inputs do the same job, come with keyboard support, and ship nothing.
 */
export function TargetDemo() {
  const [profitPercent, setProfitPercent] = useState(25);
  const [stopPercent, setStopPercent] = useState(5);

  const price = useCoinPrice("bitcoin");
  const buyPrice = price.data ?? FALLBACK_BTC_USD;

  const targetPrice = targetPriceFromPercent(buyPrice, profitPercent, "LONG");
  const stopPrice = targetPriceFromPercent(buyPrice, -stopPercent, "LONG");

  // A real `OpenTrade`, so `Bracket` receives exactly what it does in the app.
  const demoTrade: OpenTrade = {
    id: "demo",
    assetType: "crypto",
    coinBase: "bitcoin",
    coinTarget: "USD",
    tradeType: "LONG",
    dateAdded: new Date(),
    updatedAt: new Date(),
    exits: [],
    buy: { date: new Date(), price: buyPrice, quantity: QUANTITY },
    target: { profit: targetPrice, stopLoss: stopPrice },
  };

  const upside = (targetPrice - buyPrice) * QUANTITY;
  const downside = (buyPrice - stopPrice) * QUANTITY;

  return (
    <section>
      <div className="mx-auto max-w-4xl px-6 pt-16">
        <p className="eyebrow">Try it · live Bitcoin price</p>
        <h2 className="mt-2 text-3xl font-black tracking-tighter sm:text-4xl">
          Say you buy {QUANTITY} BTC right now.
        </h2>
        <p className="mt-3 max-w-xl">
          Where do you get out? Drag both handles. This is the same view you get on every trade
          you log.
        </p>
      </div>

      {/* The real bracket, on the real yellow plan field. */}
      <div className="mt-10">
        <Bracket trade={demoTrade} currentPrice={buyPrice} quoteCurrency="USD" />
      </div>

      <div className="mx-auto max-w-4xl px-6 pb-16">
        <div className="grid gap-8 border-x-2 border-b-2 border-ink bg-paper-raised p-6 sm:grid-cols-2">
          <Slider
            id="demo-target"
            label="Take profit at"
            value={profitPercent}
            min={5}
            max={100}
            onChange={setProfitPercent}
            caption={`+${profitPercent}% · ${formatPreferredCurrency(upside, "usd")} if it hits`}
            tone="gain"
          />
          <Slider
            id="demo-stop"
            label="Cut the loss at"
            value={stopPercent}
            min={1}
            max={50}
            onChange={setStopPercent}
            caption={`-${stopPercent}% · ${formatPreferredCurrency(downside, "usd")} at risk`}
            tone="loss"
          />
        </div>

        <p className="mt-8 max-w-xl">
          {price.isError
            ? "Live prices are down right now, so this demo is running on a sample Bitcoin price. "
            : ""}
          Picking the numbers is the easy part. Hitting them is the whole game.
        </p>

        <Link to="/user/start" className="btn-accent mt-6">
          Plan a real trade
        </Link>
      </div>
    </section>
  );
}

function Slider({
  id,
  label,
  value,
  min,
  max,
  onChange,
  caption,
  tone,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  caption: string;
  tone: "gain" | "loss";
}) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-ink"
      />
      <p className={`figure mt-2 text-sm font-semibold ${tone === "gain" ? "text-gain" : "text-loss"}`}>
        {caption}
      </p>
    </div>
  );
}
